import { mapMutations, mapState } from 'vuex';

import { CLEAR_SELECTED_ELEMENTS, SET_CHANGED_DATA } from '@/store/types/mutation-types';
import { getAtomClass } from '@/data/data-models';
import { generateRendering } from '@/utils/rendering';
import flexDeepCopy from '@/utils/flex-deep-copy';
import cutCopyPasteMixin from '@/mixins/cut-copy-paste';

const publicMode = process.env.VUE_APP_ENV === 'public';

export default {
    mixins: [cutCopyPasteMixin],

    computed: {
        ...mapState({
            copiedElements: state => state.page.copiedElements,
            selectedElements: state => state.page.selectedElements,
        }),

        isComponentEditor() {
            return this.$route.name === 'components';
        },
    },

    methods: {
        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
            setChangedData: SET_CHANGED_DATA,
        }),

        async onDrop(data) {
            let newElement;
            const pointer = this.elementsNew.length + 1;
            const isDataComponent = data.props && data.props.componentItemID;

            if (data.name.startsWith('a-')) {
                newElement = getAtomClass(data, pointer);
            } else {
                newElement = await generateRendering(data, pointer);
            }

            // strip out references to the original component, for data-driven components that are dragged into a CE drop zone
            if (isDataComponent && this.isComponentEditor) {
                newElement.linkedComponent = '00000000-0000-0000-0000-000000000000';
                newElement.lockComponent = false;
            }

            this.elementsNew.push(newElement);
            this.setChangedData(true);
            this.$emit('drop');
        },

        openCopyMenu(event, element) {
            if (!publicMode) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (!this.isComponentScreen && !this.selectedElements.length) return;

            this.$root.$emit('flex::context::open', {
                event,
                id: 'cut-copy-context-menu',
                data: {
                    element,
                    onCutCallback: this.deleteElement,
                },
            });
        },

        openPasteMenu(event, index) {
            if (!publicMode) {
                event.preventDefault();
                event.stopPropagation();
            }

            this.$root.$emit('flex::context::open', {
                event,
                id: 'paste-context-menu',
                data: {
                    index,
                    onPasteCallback: this.pasteElement,
                },
            });
        },

        async moveElement(direction, index) {
            let to = index;

            if (direction === 'up') {
                to = index - 1;
            } else {
                to = index + 1;
            }

            if (to < 0 || to === this.elementsNew.length) return;

            const element = this.elementsNew[index];

            this.elementsNew.splice(index, 1);
            this.elementsNew.splice(to, 0, element);
            this.setChangedData(true);

            await this.$nextTick();

            this.$root.$emit('flex::component::move');
            this.$emit('delete');
        },

        pasteElement() {
            const { elements, mode } = this.copiedElements;

            if (mode === 'cut') this.onCutCallback();

            if (mode === 'copy') {
                elements.forEach((elem) => {
                    const copy = flexDeepCopy({ ...elem });

                    this.elementsNew.push(copy);
                });
            } else {
                elements.forEach((elem) => {
                    const copy = { ...elem };

                    this.elementsNew.push(copy);
                });
            }

            this.afterPaste();
        },

        deleteElement(element) {
            if (!element) return;

            const elementIndex = this.elementsNew.findIndex(elementNew => elementNew.id === element.id);

            this.deleteElementByIndex(elementIndex);
        },

        deleteElementByIndex(index) {
            this.elementsNew.splice(index, 1);
            this.clearSelectedElements();
            this.setChangedData(true);
            this.$emit('delete');
        },
    },
};
